<template>
  <div>
    <div class="d-flex flex-wrap" v-if="!isLoading">
      <template v-if="showPinnedFilters">
        <v-chip
          v-if="hasActiveFilter && !activeFilterIsPinned"
          class="user-filter-chip cursor-pointer mr-2 mt-2"
          color="blue lighten-2"
          @click="handleRemoveActiveFilter"
        >
          <span class="blue--text caption">{{ activeFilter.name }}</span>
          <v-icon class="ml-1" color="blue" small>mdi-close</v-icon>
        </v-chip>

        <template v-else>
          <v-tooltip bottom v-for="filter in pinnedFilters" :key="filter._id">
            <template v-slot:activator="{ on }">
              <v-chip
                v-on="hasTruncate(filter.name) ? on : null"
                class="user-filter-chip cursor-pointer mr-2 mt-2"
                :color="
                  isSameOfActiveFilter(filter)
                    ? 'blue lighten-2'
                    : 'blue-grey lighten-3'
                "
                @click="onSelectFilter(filter)"
              >
                <v-icon
                  class="mr-1"
                  :color="
                    isSameOfActiveFilter(filter) ? 'blue' : 'blue-grey darken-1'
                  "
                  small
                  >mdi-pin</v-icon
                >
                <span
                  class="caption text-truncate"
                  :class="
                    isSameOfActiveFilter(filter)
                      ? 'blue--text'
                      : 'blue-grey--text text--darken-1'
                  "
                  >{{ truncateText(filter.name) }}</span
                >
              </v-chip>
            </template>
            {{ filter.name }}
          </v-tooltip>
        </template>
      </template>
      <template v-else>
        <v-chip
          v-if="hasActiveFilter"
          class="user-filter-chip cursor-pointer mr-2 mt-2"
          color="blue lighten-2"
          @click="handleRemoveActiveFilter"
        >
          <span class="blue--text caption">{{ activeFilter.name }}</span>
          <v-icon class="ml-1" color="blue" small>mdi-close</v-icon>
        </v-chip>
      </template>
    </div>
    <div class="d-flex mt-2" v-else>
      <v-skeleton-loader
        class="skeleton-user-filter-chip mr-2"
        type="chip"
      ></v-skeleton-loader>
      <v-skeleton-loader
        class="skeleton-user-filter-chip mr-2"
        type="chip"
      ></v-skeleton-loader>
      <v-skeleton-loader
        class="skeleton-user-filter-chip mr-2"
        type="chip"
      ></v-skeleton-loader>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'

import api from '@/api'
import { mapGetters } from 'vuex'
import trackingMixin from '@/mixins/tracking/trackerMixin.vue'

const MAX_NAME_LENGTH = 28

export default {
  mixins: [trackingMixin],

  data() {
    return {
      pinnedFilters: [],
      isLoading: false,
    }
  },
  props: {
    initialFilters: Object,
    showPinnedFilters: {
      type: Boolean,
      default: true,
    },
  },
  created() {
    if (this.showPinnedFilters) this.fetchPinnedFilters()
  },
  computed: {
    ...mapGetters(['organizationId']),
    activeFilter() {
      return {
        _id: this.initialFilters?.id ?? this.$route.query.userFilter,
        name: this.initialFilters?.name ?? this.$route.query.userFilterName,
      }
    },
    hasActiveFilter() {
      return Boolean(this.activeFilter._id && this.activeFilter.name)
    },
    activeFilterIsPinned() {
      return Boolean(
        this.pinnedFilters?.find(
          filter => filter?._id === this.activeFilter._id
        )
      )
    },
  },
  methods: {
    handleRemoveActiveFilter() {
      this.$emit('onRemoveActiveFilter')
      this.trackFilterActions('my_filters.resetFilter', {
        section: this.$route.name,
      })
    },

    async fetchPinnedFilters() {
      try {
        this.isLoading = true
        const response = await api.userFilter.getAll(this.organizationId, {
          filters: { isPinned: true },
        })
        this.pinnedFilters = response?.data?.results
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },
    onSelectFilter(filter) {
      if (this.isSameOfActiveFilter(filter))
        return this.$emit('onRemoveActiveFilter')

      this.$emit('onSelectFilter', {
        userFilter: filter._id,
        userFilterName: filter.name,
        ..._.omit(filter.filters, '_id'),
      })
    },
    isSameOfActiveFilter(filter) {
      return Boolean(filter._id === this.activeFilter._id)
    },
    hasTruncate(name = '') {
      return name.length > MAX_NAME_LENGTH
    },
    truncateText(name = '') {
      if (!this.hasTruncate(name)) return name
      return `${name.substring(0, MAX_NAME_LENGTH)}...`
    },
  },
}
</script>

<style lang="scss">
.skeleton-user-filter-chip .v-skeleton-loader__chip {
  width: 150px;
}
.user-filter-chip .text-truncate {
  max-width: 175px;
}
</style>
